import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { MdClose, MdOutlineErrorOutline } from 'react-icons/md';

import { AmountFilter, AmountFilterValue } from './AmountFilter';
import { DateFilter, DateFilterValue } from './DateFilter';
import { FilterDropdown } from './FilterDropdown';
import { MultiSelectFilter, MultiSelectFilterValue } from './MultiSelectFilter';
import { SelectFilter, SelectFilterValue } from './SelectFilter';
import { TextFilter, TextFilterValue } from './TextFilter';
import { BlueButton, CloseButton, Heading, TurquoiseButton } from './styles';
import { FilterOption, FilterOptionError } from './types';

interface Props {
  filterOptionValues: FilterOption[];
  initFilterOptionValues: FilterOption[];
  setFilterOptionValues(filterOptionsValues: FilterOption[]): void;
  toggleFilter: () => void;
  handlePageChange(page: number): void;
  handleFilterCount(count: number): void;
}

function FilterComponent({
  filterOptionValues,
  initFilterOptionValues,
  setFilterOptionValues,
  toggleFilter,
  handlePageChange,
  handleFilterCount,
}: Props) {
  const CloseIcon = MdClose as FunctionComponent;
  const OutlineErrorOutlineIcon = MdOutlineErrorOutline as FunctionComponent;

  const [filterOptionValuesObj, setFilterOptionValuesObj] = useState<FilterOption[]>(filterOptionValues);
  const [filterOptionErrors, setFilterOptionErrors] = useState<FilterOptionError[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const filterChangeHandler = useCallback((newOption: FilterOption) => {
    return setFilterOptionValuesObj((prevOptionValues) =>
      prevOptionValues.map((option) =>
        option.option_id === newOption.option_id
          ? newOption.checked
            ? newOption
            : { ...newOption, value: null } // Reset value if unchecked
          : option,
      ),
    );
  }, []);

  const validate = useCallback(() => {
    const filterObjErrors: FilterOptionError[] = [];
    const isRequiredError = 'This field is required';
    const selectAnOptionError = 'Please select an option';

    filterOptionValuesObj.forEach((option) => {
      if (option.checked) {
        if (option.optionType === 'amount' && !option.value) {
          filterObjErrors.push({ option_id: option.option_id, error: isRequiredError });
        }
        if (option.optionType === 'date' && !option.value) {
          filterObjErrors.push({ option_id: option.option_id, error: isRequiredError });
        }
        if (option.optionType === 'select' && !option.value) {
          filterObjErrors.push({ option_id: option.option_id, error: isRequiredError });
        }
        if (option.optionType === 'multi_select' && !option.value) {
          filterObjErrors.push({ option_id: option.option_id, error: selectAnOptionError });
        }
        if (option.optionType === 'text' && !option.value) {
          filterObjErrors.push({ option_id: option.option_id, error: isRequiredError });
        }
      }
    });

    setFilterOptionErrors(filterObjErrors);
    return filterObjErrors.length;
  }, [filterOptionValuesObj]);

  const filterSaveHandler = useCallback(async () => {
    if (validate()) return;

    setFilterOptionValues(filterOptionValuesObj);
    handlePageChange(1);
    toggleFilter();
  }, [filterOptionValuesObj, handlePageChange, setFilterOptionValues, toggleFilter, validate]);

  const clearFunction = useCallback(() => {
    setFilterOptionErrors([]);
    setFilterOptionValuesObj(initFilterOptionValues);
  }, [initFilterOptionValues]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        toggleFilter();
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [toggleFilter]);

  useEffect(() => {
    handleFilterCount(filterOptionValuesObj.filter((option) => option.checked).length);
  }, [filterOptionValuesObj, handleFilterCount]);

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <FilterDropdown className="filter-dropdown">
        <div className="lp-flex lp-flex-column" data-testid="filter-dropdown">
          <div className="filter-header mb-0">
            <Heading className="mb-0">Filters</Heading>
            <CloseButton type="button" onClick={() => toggleFilter()}>
              <CloseIcon />
            </CloseButton>
          </div>
          <div className="filter-body">
            {filterOptionValuesObj.map((option) => (
              <div className="filter-option" key={option.option_id}>
                <div className="filter-input">
                  <input
                    type="checkbox"
                    id={`${option.option_id}-filter-checkbox`}
                    checked={option.checked}
                    onChange={(e) => filterChangeHandler({ ...option, checked: e.target.checked })}
                    data-testid={`${option.option_id}-filter-checkbox`}
                  />
                  <label className="filter-checkbox-label" htmlFor={`${option.option_id}-filter-checkbox`}>
                    {option.label}
                  </label>
                </div>

                {option.checked && (
                  <div className="checkbox-items-details">
                    {option.optionType === 'select' && (
                      <SelectFilter
                        value={option.value as SelectFilterValue}
                        options={option.optionConfig?.options || []}
                        onChange={(value) => filterChangeHandler({ ...option, value })}
                      />
                    )}
                    {option.optionType === 'multi_select' && (
                      <MultiSelectFilter
                        value={option.value as MultiSelectFilterValue}
                        options={option.optionConfig?.options}
                        onChange={(value) => filterChangeHandler({ ...option, value })}
                      />
                    )}
                    {option.optionType === 'date' && (
                      <DateFilter
                        value={option.value as DateFilterValue}
                        conditions={option.optionConfig?.conditions}
                        onChange={(value) => filterChangeHandler({ ...option, value })}
                      />
                    )}
                    {option.optionType === 'amount' && (
                      <AmountFilter
                        value={option.value as AmountFilterValue}
                        conditions={option.optionConfig?.conditions}
                        onChange={(value) => filterChangeHandler({ ...option, value })}
                      />
                    )}
                    {option.optionType === 'text' && (
                      <TextFilter
                        value={option.value as TextFilterValue}
                        conditions={option.optionConfig?.conditions}
                        onChange={(value) => filterChangeHandler({ ...option, value })}
                      />
                    )}
                    {filterOptionErrors.map(
                      (element) =>
                        element.option_id === option.option_id && (
                          <div className="option-error">
                            <OutlineErrorOutlineIcon /> {element.error}
                          </div>
                        ),
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="filter-footer">
            <TurquoiseButton className="lp-w-full" onClick={() => clearFunction()}>
              Clear
            </TurquoiseButton>
            <BlueButton className="lp-w-full" onClick={() => filterSaveHandler()}>
              Save
            </BlueButton>
          </div>
        </div>
      </FilterDropdown>
    </div>
  );
}

export default FilterComponent;
